/**
 * SbSelect.jsx
 *
 * SB-branded select component.
 * Pattern mirrors cfSelect.tsx from cf-webapp-2.0-master,
 * adapted for JavaScript and SB_STYLED_THEME (#008080 primary).
 *
 * Key features vs plain antd Select:
 *  - Value validation  — automatically clears stale values not present in options
 *  - Multi-mode guard  — for 'multiple'/'tags' modes, filters out missing values
 *                        and fires onChange to keep parent state consistent
 *  - PopupContainer    — defaults to triggerNode.parentNode to prevent scroll
 *                        context issues (mirrors cfSelect behaviour)
 *  - SB brand styling  — teal focus ring, teal hover border, teal option highlight
 *  - data passthrough  — onChange(value, data) carries extra payload to parent
 *
 * ─── Props (on top of standard antd SelectProps) ─────────────────────────────
 *
 *   onChange            (value: string, data: any) => void
 *   onMultiModeChange   (values: string[], data: any) => void
 *   data                any   — passed as second arg to onChange callbacks
 *   clearStaleValue     bool  — default true (legacy). When false, a value not
 *                               present in options is displayed as-is and no
 *                               callback is fired during render.
 *   showSearch          bool  — default true
 *   block               bool  — makes the select full-width (like antd's block on Button)
 *
 * ─── Usage ────────────────────────────────────────────────────────────────────
 *
 *   // Single select
 *   <SbSelect
 *     value={role}
 *     options={roleOptions}
 *     onChange={(val, data) => setRole(val)}
 *   />
 *
 *   // Multi select
 *   <SbSelect
 *     mode="multiple"
 *     value={skills}
 *     options={skillOptions}
 *     onMultiModeChange={(vals, data) => setSkills(vals)}
 *     block
 *   />
 *
 *   // Borderless (for sub-tab selectors)
 *   <SbSelect variant="borderless" value={tab} onChange={setTab} options={tabOptions} />
 */

import React, { useMemo, useCallback } from 'react';
import { Select } from 'antd';

// ── Dropdown mode constants ────────────────────────────────────────────────────
export const DROPDOWN_MODE = {
  MULTIPLE: 'multiple',
  TAGS: 'tags',
};

// ─────────────────────────────────────────────────────────────────────────────

const SbSelect = (props) => {
  const {
    data,
    onChange,
    onMultiModeChange,
    disabled,
    getPopupContainer: customGetPopupContainer,
    value: selectedValue,
    options = [],
    mode,
    notFoundContent,
    onBlur,
    clearStaleValue = true,
    ...rest
  } = props;

  // Default popup container — attach to trigger's parent to avoid scroll issues
  const getPopupContainer = useMemo(
    () =>
      customGetPopupContainer
        ? customGetPopupContainer
        : (triggerNode) => triggerNode?.parentNode || document.body,
    [customGetPopupContainer],
  );

  // ── Value validation ────────────────────────────────────────────────────────

  const getValidSelectedValueByMode = () => {
    if (mode) {
      // Multiple/tags: filter out any values no longer present in options
      return mode === DROPDOWN_MODE.MULTIPLE
        ? (Array.isArray(selectedValue) ? selectedValue : []).filter(item =>
          options?.some(option => option.value === item),
        )
      : selectedValue || [];
    } else {
      // Single: confirm the value exists in options
      return options?.find(item => item?.value === selectedValue);
    }
  };

  const handleSingleModeSelect = (validSelectedValue) => {
    // Only clear a stale value when the options list is populated — if options
    // haven't loaded yet (empty array) the value may still be valid once they do.
    if (selectedValue && !validSelectedValue && options?.length > 0) {
      onChange && onChange('', data);
    }
    return validSelectedValue ? selectedValue : undefined;
  };

  const handleMultiModeSelect = (validSelectedValue) => {
    // Only notify parent of filtered-out values when options are loaded.
    if (
      selectedValue?.length > 0 &&
      selectedValue?.length !== validSelectedValue?.length &&
      options?.length > 0
    ) {
      onMultiModeChange && onMultiModeChange(validSelectedValue, data);
    }
    return validSelectedValue;
  };

  const getValidSelectedValue = () => {
    // Opt-out: render exactly what we were given and never call the parent during
    // render. For screens that already clear stale values in their own cascade.
    if (!clearStaleValue) {
      if (!mode) return selectedValue;
      return Array.isArray(selectedValue) ? selectedValue : (selectedValue ? [selectedValue] : []);
    }
    const valid = getValidSelectedValueByMode();
    return mode ? handleMultiModeSelect(valid) : handleSingleModeSelect(valid);
  };

  // ── Change / blur handlers ─────────────────────────────────────────────────

  const handleChange = useCallback(
    (value) => {
      // Call custom onChange if provided
      if (onChange) {
        onChange(value, data);
      }
      // Call onMultiModeChange for multi-select
      if (mode && onMultiModeChange) {
        onMultiModeChange(value, data);
      }
    },
    [mode, onChange, onMultiModeChange, data],
  );

  const handleBlur = useCallback(
    (e) => {
      onBlur && onBlur(e, data);
    },
    [onBlur, data],
  );

  // Prevent dropdown opening for tags mode with no options
  const shouldHideDropdown = mode === DROPDOWN_MODE.TAGS && options.length === 0;

  return (
    // <SbSelectWrapperStyled $block={block} style={block ? { width: '100%' } : undefined}>
    <Select
      {...rest}
      disabled={disabled}
      mode={mode}
      getPopupContainer={getPopupContainer}
      onChange={handleChange}
      onBlur={handleBlur}
      value={getValidSelectedValue()}
      notFoundContent={shouldHideDropdown ? null : notFoundContent}
      options={options}
    />
    // </SbSelectWrapperStyled>
  );
};

export default SbSelect;
